import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import axios from 'axios';

import CardContainer from '../../common/CardContainer';
import { Text } from '../../common/Text';
import { openFileViewer } from '../../../actions';
import StyledTappable from '../../common/StyledTappable';
import downloadIcon from '../../../images/download.svg';
import config from '../../../config';
import { showError } from '../../../utils';

const OuterContainer = styled(CardContainer)`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: flex-start;
  margin: 24px 20px 0px 24px;
  background: #ffffff;
  border: 1px solid #E4E4F4;
  box-sizing: border-box;
  border-radius: 12px;
  max-width: 80%;
  overflow: hidden;
`;

const HeaderContainer = styled.div`
  display: flex;
  align-self: stretch;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  background: #F5F3FF;
  border-bottom: 1px solid #E4E4F4;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PillIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #EDE9FE;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const HeaderText = styled(Text)`
  font-weight: 700;
  font-size: 14px;
  color: #4F46E5;
`;

const DownloadContainer = styled(StyledTappable)`
  display: flex;
  align-items: center;
  gap: 4px;
  color: #4F46E5;
  font-weight: 600;
  font-size: 11px;
`;

const DownloadIcon = styled.img`
  width: 16px;
  height: 16px;
`;

const BodyContainer = styled.div`
  align-self: stretch;
  padding: 12px 16px;
`;

const SupplementItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #F0F3F6;
  &:last-child {
    border-bottom: none;
  }
`;

const SupplementIcon = styled.div`
  flex-shrink: 0;
  margin-top: 2px;
`;

const SupplementInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const SupplementName = styled(Text)`
  font-weight: 600;
  font-size: 13px;
  color: #0F0B28;
`;

const SupplementDetail = styled(Text)`
  font-weight: 400;
  font-size: 11px;
  color: #767787;
  margin-top: 2px;
`;

const OrderButton = styled(StyledTappable)`
  align-self: stretch;
  background: ${(props) => (props.loading ? '#f0f3f6' : '#4F46E5')};
  border-radius: 10px;
  margin: 12px 16px 16px;
  padding: 10px 0;
  text-align: center;
  font-weight: 600;
  font-size: 13px;
  color: ${(props) => (props.loading ? '#585969' : '#ffffff')};
`;

const SupplementMessage = ({ supplements, prescriptionLink, prescriptionId }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const authToken = useSelector((state) => state.user.authToken);
  const [loading, setLoading] = useState(false);

  const handleDownload = () => {
    if (window.Android) {
      return window.open(prescriptionLink, '_blank');
    } else if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(
        JSON.stringify({ method: 'openUrl', link: prescriptionLink })
      );
    } else {
      dispatch(
        openFileViewer({
          contentUrl: prescriptionLink,
          title: 'Supplements',
          type: 'pdf',
        })
      );
    }
  };

  const handleOrderSupplements = async () => {
    if (loading || !prescriptionId) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${config.pharmaServer}/v2/order-medicine/initiate-order`,
        { prescriptionId, type: 'supplement' },
        { headers: { authorization: authToken } }
      );
      const { data } = response.data;
      if (data && data.cartId) {
        history.push('/pharmacy/medicine/cart', {
          cartId: data.cartId,
          addressId: data.addressId,
        });
      }
    } catch (err) {
      console.error('Error initiating supplement order', err);
      showError(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <OuterContainer>
      <HeaderContainer>
        <HeaderLeft>
          <PillIcon>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2">
              <path d="M4.5 19.5 12 12M14.5 4.5l5 5-10 10-5-5z"/>
            </svg>
          </PillIcon>
          <HeaderText>Supplements</HeaderText>
        </HeaderLeft>
        <DownloadContainer onTap={handleDownload}>
          <DownloadIcon src={downloadIcon} />
          <span>Download</span>
        </DownloadContainer>
      </HeaderContainer>

      <BodyContainer>
        {supplements && supplements.length > 0 && supplements.map((s, idx) => (
          <SupplementItem key={idx}>
            <SupplementIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#4F46E5" strokeWidth="2">
                <path d="M4.5 19.5 12 12M14.5 4.5l5 5-10 10-5-5z"/>
              </svg>
            </SupplementIcon>
            <SupplementInfo>
              <SupplementName>{s.name || s.drugName || ''}</SupplementName>
              {s.notes && <SupplementDetail>{s.notes}</SupplementDetail>}
              {!s.notes && s.dosage && s.dosage !== '-' && (
                <SupplementDetail>Qty: {s.dosage}</SupplementDetail>
              )}
            </SupplementInfo>
          </SupplementItem>
        ))}
      </BodyContainer>

      <OrderButton loading={loading} onTap={handleOrderSupplements}>
        {loading ? 'Loading...' : 'Order Supplements'}
      </OrderButton>
    </OuterContainer>
  );
};

export default SupplementMessage;
